/**
 * useCurrencyContext — Phase 84.22.8 (Wave B1 · Foundation).
 *
 * The canonical source-of-truth for every "display an amount" decision in
 * the app. Wraps a React Context so pages don't each re-fetch the same
 * data (accounts + FX rates + base_currency); one fetch at App root
 * covers everything.
 *
 * The two display rules — encoded once, everywhere:
 *   • Single-row detail  → formatNative(amount, row.currency)
 *   • Aggregate ≥ 2 rows → sumInBase(rows) then formatBase(total)
 *
 * Usage:
 *   const ctx = useCurrencyContext();
 *   // In an aggregate widget:
 *   const total = ctx.sumInBase(txns, 'amount', 'currency');
 *   return <div>{ctx.formatBase(total)}</div>;
 *
 *   // In a per-row cell:
 *   return <div>{ctx.formatNative(t.amount, t.currency)}</div>;
 *
 *   // For a transaction whose currency lives on the linked account:
 *   const cur = ctx.currencyOfAccount(t.account_id);
 *   return <div>{ctx.formatNative(t.amount, cur)}</div>;
 *
 * The context is auth-scoped: `useAuth()` supplies the user id; we
 * re-fetch whenever the user changes so we never leak another user's
 * FX map across tabs.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { formatMoney } from '@/lib/format';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';
import { readCache, writeCache } from '@/lib/pageCache';

const CACHE_KEY = 'currency-context';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CTX = {
  base: 'INR',
  // Whether anybody CHOSE that base. `base` always has a value because
  // arithmetic needs a unit; `baseChosen` is the separate question of whether
  // we may present a consolidated total in it. See lib/currency_context.py.
  baseChosen: false,
  baseProvenance: 'fallback',
  dataCurrencies: [],
  // Empty rate map on purpose — consumers must gate on `isReady` (or
  // check whether a currency is present) before showing a converted
  // amount. If we defaulted to `{INR: 1.0}` an INR row on an AED
  // profile would falsely render "≈ AED 500,000" during the split
  // second before the real rates load (bug caught in Wave B3 QA).
  ratesToBase: {},
  accountCurrencyMap: {},
  asOfDate: null,
  source: null,
  staleDays: 0,
  warnings: [],
  isReady: false,
  canConsolidate: false,
  reload: () => {},
};

const CurrencyContextObj = createContext(DEFAULT_CTX);

/**
 * <CurrencyContextProvider> — mount ONCE at the App root, inside
 * <AuthProvider>. All descendant components can `useCurrencyContext()`.
 */
export function CurrencyContextProvider({ children }) {
  const { user, session } = useAuth();
  const [state, setState] = useState(DEFAULT_CTX);
  // Bumped by reload(). Without it the fetch effect only fired on a uid/token
  // change, so this file's own promise — "if a user changes their
  // base_currency the hook invalidates and refetches" — was not kept, and a
  // freshly chosen reporting currency stayed invisible until a remount.
  const [refetchNonce, setRefetchNonce] = useState(0);
  const uid = user?.id;
  const token = session?.access_token;

  // Phase 84.22.10 (Wave B3) — direct useEffect (not useCallback +
  // useEffect) so the load function isn't reinstantiated on every
  // session touch. That churn was firing an axios request on every
  // token refresh, producing a cascade of ERR_ABORTED entries in the
  // console. Now we fire ONCE per uid change; the initial `undefined`
  // token is handled by a supabase.auth.getSession() fallback.
  useEffect(() => {
    let cancelled = false;
    if (!uid) return;
    const controller = new AbortController();
    // Show the last confirmed rates immediately. Without this, every converted
    // figure and every aggregate total popped in a second late and nudged the
    // layout as it arrived.
    const cached = refetchNonce === 0 ? readCache(uid, CACHE_KEY) : null;
    if (cached) setState((prev) => ({ ...prev, ...cached, isReady: true, reload: prev.reload }));
    (async () => {
      try {
        let jwt = token;
        if (!jwt) {
          const { data } = await supabase.auth.getSession();
          jwt = data?.session?.access_token;
        }
        const r = await axios.get(`${BACKEND_URL}/api/currency/context`, {
          headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
          signal: controller.signal,
        });
        if (cancelled) return;
        const d = r.data || {};
        writeCache(uid, CACHE_KEY, {
          base: (d.base || 'INR').toUpperCase(),
          baseChosen: Boolean(d.base_chosen),
          baseProvenance: d.base_provenance || 'fallback',
          dataCurrencies: d.data_currencies || [],
          ratesToBase: d.rates_to_base || {},
          accountCurrencyMap: d.account_currency_map || {},
          asOfDate: d.as_of_date || null,
          source: d.source || null,
          staleDays: d.stale_days ?? 0,
          warnings: d.warnings || [],
        });
        setState((prev) => ({
          base:               (d.base || 'INR').toUpperCase(),
          baseChosen:         Boolean(d.base_chosen),
          baseProvenance:     d.base_provenance || 'fallback',
          dataCurrencies:     d.data_currencies || [],
          ratesToBase:        d.rates_to_base || {},
          accountCurrencyMap: d.account_currency_map || {},
          asOfDate:           d.as_of_date || null,
          source:             d.source || null,
          staleDays:          d.stale_days ?? 0,
          warnings:           d.warnings || [],
          isReady:            true,
          reload:             prev.reload,
        }));
      } catch (e) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        console.warn('[useCurrencyContext] load failed:', e?.message || e);
        if (!cancelled) {
          // Keep previous rates if we had any; otherwise mark ready to
          // unblock the UI (aggregates will render "—" per the guard
          // in CategoryTab / OverviewTab until a successful reload).
          setState((prev) => (prev.isReady ? prev : { ...prev, isReady: true }));
        }
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [uid, token, refetchNonce]);

  // Expose a manual reload for callers who need to force-refresh (e.g.
  // after the user changes their base currency in Settings).
  const reload = useCallback(() => {
    setState((prev) => ({ ...prev, isReady: false }));
    setRefetchNonce((n) => n + 1);
  }, []);
  useEffect(() => {
    // Attach reload once so consumers can call ctx.reload() without
    // re-rendering when state flips isReady false→true.
    setState((prev) => (prev.reload === reload ? prev : { ...prev, reload }));
  }, [reload]);

  // Bind helper functions to the current state — memoised so consumers
  // don't re-render on unrelated context updates.
  const value = useMemo(() => {
    const { base, ratesToBase, accountCurrencyMap } = state;
    const toBase = (amount, currency) => {
      const amt = Number(amount || 0);
      const cur = (currency || '').toUpperCase();
      if (!cur) return amt;
      const rate = ratesToBase[cur];
      return rate == null ? amt : amt * rate;
    };
    const sumInBase = (rows, amountKey = 'amount', currencyKey = 'currency') => {
      let t = 0;
      for (const r of (rows || [])) t += toBase(r?.[amountKey], r?.[currencyKey]);
      return t;
    };
    const currencyOfAccount = (accountId) =>
      accountCurrencyMap[accountId] || base;
    const formatNative = (amount, currency, opts = {}) =>
      formatMoney(Number(amount || 0), (currency || base).toUpperCase(), opts);
    const formatBase = (amount, opts = {}) =>
      formatMoney(Number(amount || 0), base, opts);
    // May we state a combined figure at all? Yes if the person chose the
    // reporting currency, and yes if every figure they hold is already in one
    // and the same currency (a sum, not a conversion — nothing is assumed).
    // Otherwise the total needs a decision nobody has made, and inventing one
    // is the defect this exists to prevent.
    const canConsolidate = state.baseChosen || (state.dataCurrencies || []).length === 1;
    return {
      ...state,
      canConsolidate,
      toBase,
      sumInBase,
      currencyOfAccount,
      formatNative,
      formatBase,
    };
  }, [state]);

  return (
    <CurrencyContextObj.Provider value={value}>
      {children}
    </CurrencyContextObj.Provider>
  );
}

/**
 * Any component beneath <CurrencyContextProvider> can call this to get
 * the memoised context object. Before the provider has finished loading,
 * `isReady` is false — most callers can ignore that flag and rely on
 * the DEFAULT_CTX fallback (INR base, empty maps) since it renders
 * gracefully.
 */
export function useCurrencyContext() {
  return useContext(CurrencyContextObj);
}
